import PageHead from '@components/PageHead';
import renderContentsToText from '@helpers/renderContentsToText';
import config from 'config';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import CommentCard from '../../components/CommentCard';
import CommentInput from '../../components/CommentInput';
import EpisodePlayer from '../../components/EpisodePlayer';
import renderDisplayDate from '../../helper/renderDisplayDate';
import useAPI from '../../hooks/useAPI';
import useErrorHandler from '../../hooks/useErrorHandler';
import Comment from '../../models/Comment';
import Episode from '../../models/Episode';
import StickyNav from './StickyNav';
import useSession from '@hooks/useSession';

function EpisodeDetailPage(props: { episode: Episode }) {
  const session = useSession();
  const preloadEpisode = props.episode;
  const API = useAPI();
  const errorHandler = useErrorHandler();
  const router = useRouter();

  const [episode, setEpisode] = useState(preloadEpisode);
  const [comments, setComments] = useState<Comment[]>([]);

  async function refreshComments() {
    try {
      const res = await API.getComments(episode.id);
      setComments(res);
    } catch (err) {
      errorHandler.handle(err);
    }
  }

  async function refreshEpisode() {
    try {
      const res = await API.episode.get(episode.id);
      if (res.is_purchased || res.free_listen) {
        res.description = preloadEpisode.description;
      } else {
        res.description = '<p>僅訂購用戶可見，如已訂購請先使用原訂購帳戶登入</p>';
      }
      setEpisode(res);
    } catch (err) {
      errorHandler.handle(err);
    }
  }

  useEffect(() => {
    refreshEpisode();
    refreshComments();
  }, []);

  useEffect(() => {
    refreshEpisode();
  }, [session.isLoggedIn]);

  return (
    <div>
      <PageHead
        title={episode.title + ' | 單集收聽 | Hoost'}
        description={episode.free_listen
          ? renderContentsToText(episode.contents)
          : '此為付費內容，講師隨時在課程裡等你！'}
        imageUrl={episode.image}
        pageUrl={config.DEFAULT_PAGE_URL + '/episode/' + episode.id}
      />
      <StickyNav/>
      <div className="episode-page">
        <div className="section-basic">
          <div className="container-1200 _2-col w-container">
            <div className="panel-base">
              <div className="section-title-wrap">
                <div className="podcaster-info-wrap">
                  <div className="podcaster-photo">
                    <img
                      onClick={() =>
                        router.push('/podcast/' + episode.podcast.id)
                      }
                      src={episode.podcast.image}
                      loading="lazy"
                      alt=""
                      className="image-2"
                    />
                  </div>
                  <div className="podcastor-name-wrap">
                    <h5
                      className="h5 mb4"
                      onClick={() =>
                        router.push('/podcast/' + episode.podcast.id)
                      }
                    >
                      {episode.podcast.title}
                    </h5>
                    <div className="div-block">
                      <p className="episode-release-time">
                        {renderDisplayDate(new Date(episode.created_at), false)}
                      </p>
                      <p className="number-of-listens">
                        {episode.listen_times} 次收聽
                      </p>
                    </div>
                  </div>
                </div>
                <div className="section-title-underline"/>
              </div>
              <EpisodePlayer episode={episode} isDarkMode={false}/>
              <div className="section-title-wrap">
                <h4 className="section-title">本集圖文稿</h4>
                <div className="section-title-underline"/>
              </div>
              <div
                id="description"
                style={{
                  transition: 'max-height 0.1s ease-in-out',
                  overflow: 'hidden',
                }}
              >
                <div
                  className="render-article"
                  dangerouslySetInnerHTML={{ __html: episode.description }}
                />
              </div>
              <div className="section-title-wrap">
                <h4 className="section-title">學員留言</h4>
                <div className="section-title-underline"/>

                <div>
                  <CommentInput
                    episode={episode}
                    onRefreshComments={refreshComments}
                  />
                  <div className="comment-divider"/>
                </div>

                {comments.map((comment) => (
                  <CommentCard
                    key={comment.id}
                    comment={comment}
                    episode={episode}
                    onRefreshComments={refreshComments}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EpisodeDetailPage;
